import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import PageButtonWhite from "./PageButtonWhite";
import PageButtonBlack from "./PageButtonBlack";
import PageButtonBlue from "./PageButtonBlue";
import Grid from "@mui/material/Grid2";
import axios from "../../api/axios";
import * as XLSX from "xlsx";

export default function ModalMassiveRepresentantes(props) {
  const { open, onClose, selected = [], action } = props;
  const [loading, setLoading]     = useState(false);
  const [results, setResults]     = useState([]);
  const [processed, setProcessed] = useState(0);
  const [started, setStarted]     = useState(false);

  const successfulCount = results.filter((r) => r.status === "Exitoso").length;
  const failedCount     = results.filter((r) => r.status === "Fallido").length;

  useEffect(() => {
    if (open) {
      setResults([]);
      setProcessed(0);
      setLoading(false);
      setStarted(false);
    }
  }, [open, selected, action]);

  const callActions = useCallback(async () => {
    if (!selected.length) return;

    setStarted(true);
    setLoading(true);

    const allResults = [];
    for (const combined of selected) {
      const [rbd, run] = combined.split("_");
      try {
        if (action === "Representante") {
          await axios.tempotoralToRepresentante(run, rbd);
        } else if (action === "Deshabilitado") {
          await axios.disableRepresentante(run, rbd);
        } else {
          throw new Error(`Acción desconocida: ${action}`);
        }
        allResults.push({ id: combined, rbd, run, status: "Exitoso", detail: "" });
      } catch (err) {
        allResults.push({
          id: combined,
          rbd,
          run,
          status: "Fallido",
          detail: err.message || "Error en petición",
        });
      }
      setResults([...allResults]);
      setProcessed(allResults.length);
    }

    setLoading(false);
  }, [action, selected]);

  const downloadXLS = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(results);
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    XLSX.writeFile(wb, "resultados.xlsx");
    onClose(true, successfulCount);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={() => onClose(started, successfulCount)}
      aria-labelledby="modal-massive-title"
      sx={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      }}
      slotProps={{ backdrop: { sx: { opacity: "0.1!important" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "800px",
          maxHeight: "600px",
          bgcolor: "background.paper",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: 0,
          overflowY: "auto",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center", position: "relative" }}>
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => onClose(true, successfulCount)}
          >
            <CloseIcon />
          </IconButton>

          {!started ? (
            <Box sx={{ mb: 2 }}>
              <WarningAmberIcon color="error" sx={{ fontSize: 45 }} />
              <Typography
                variant="h2"
                component="h1"
                fontWeight="bold"
                gutterBottom
                sx={{ mb: 1, color: "#4D4D4D" }}
              >
                Acción en masa (Pasar a {action})
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }} gutterBottom>
                ¿Está seguro de pasar “{action}” los usuarios seleccionados ({selected.length})?
              </Typography>
              <Typography variant="body1" sx={{ px: 5, color: "#333" }}>
                Esta operación no se puede deshacer. Asegúrese antes de continuar.
              </Typography>

              <Grid container spacing={2} sx={{ pt: 1 }}>
                <Grid size={3} />
                <Grid size={3}>
                  <PageButtonWhite onClick={() => onClose(true, 0)} width="100%">
                    Cancelar
                  </PageButtonWhite>
                </Grid>
                <Grid size={5}>
                  <PageButtonBlack onClick={callActions} disabled={loading} width="100%">
                    OK, pasar a {action}
                  </PageButtonBlack>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              {/* Bloque restaurado de resultados con íconos */}
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontSize: "26px",
                  my: 1,
                  color: "#4D4D4D",
                  textTransform: "uppercase",
                }}
              >
                Resultados ({processed}/{selected.length})
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, my: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <DoneOutlineOutlinedIcon color="success" />
                  <Typography variant="body1" fontWeight="bold">
                    {successfulCount} Exitosos
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ErrorOutlineIcon color="error" />
                  <Typography variant="body1" fontWeight="bold">
                    {failedCount} Fallidos
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ my: 1 }}>
                <Typography variant="body1">
                  Procesados: {processed} / {selected.length}
                </Typography>
                {loading && <LinearProgress sx={{ mt: 1 }} />}
              </Box>
              {/* Fin del bloque restaurado */}

              <TableContainer
                sx={{
                  p: 1,
                  maxHeight: "300px",
                  overflow: "auto",
                  mb: 2,
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: 4 },
                  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
                }}
              >
                <Table stickyHeader sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">RBD</TableCell>
                      <TableCell align="center">RUN</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Detalle</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell align="center">{r.rbd}</TableCell>
                        <TableCell align="center">{r.run}</TableCell>
                        <TableCell align="center">
                          {r.status === "Exitoso" ? (
                            <>
                              <DoneOutlineOutlinedIcon color="success" /> Exitoso
                            </>
                          ) : (
                            <>
                              <ErrorOutlineIcon color="error" /> {r.status}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {r.status === "Exitoso"
                            ? "Acción completada"
                            : (r.detail || "-")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mb: 2, pt: 1 }}>
                {!loading ? (
                  <PageButtonBlue width="350px" onClick={downloadXLS}>
                    Cerrar y descargar resumen
                  </PageButtonBlue>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Procesando… por favor espera
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
}
